/**
 * FAQ Section - Clean accordion styling
 */

import { useState } from 'react';
import { HelpCircle, Plus, Minus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import EditableElement from '../admin/EditableElement';

// Default FAQs for fallback when API is unavailable
const defaultFaqs = [
  {
    id: '1',
    question: 'What is As-Is?',
    answer: 'As-Is is IKEA\'s section for discounted items - floor models, open-box returns, and items with minor damage. Prices are typically 50-70% off retail. Most As-Is is sold in-store only, and inventory changes daily. RUMIO is the only way to see what\'s in the As-Is section without driving to the store.',
  },
  {
    id: '2',
    question: 'How is RUMIO different from other price trackers?',
    answer: 'Most trackers only watch online prices. RUMIO monitors all IKEA sale categories - As-Is (both online and in-store), Last Chance, Family Offers, and Limited Time deals. Our network of in-store scanners feeds a real-time database of As-Is inventory that you can\'t find anywhere else.',
  },
  {
    id: '3',
    question: 'What\'s the IKEA Planner import?',
    answer: 'Planning a kitchen, PAX wardrobe, or BESTA system? Save your design from IKEA\'s planning tools and upload it to RUMIO. We\'ll automatically create a checklist of every item in your design and watch for deals on each one.',
  },
  {
    id: '4',
    question: 'Is RUMIO affiliated with IKEA?',
    answer: 'No, RUMIO is an independent service and is not affiliated with, endorsed by, or connected to IKEA in any way. IKEA is a registered trademark of Inter IKEA Systems B.V.',
  },
  {
    id: '5',
    question: 'What\'s the difference between Free, Pro, and Pro+?',
    answer: 'Free lets you track 5 items with 24-hour delayed alerts. Pro ($4.99/mo) gives you 50 items, 6-hour alerts, push notifications, and 30-day price history. Pro+ ($9.99/mo) offers unlimited items, real-time alerts, general search terms like "Gas Range", and API access.',
  },
  {
    id: '6',
    question: 'Do you have a mobile app?',
    answer: 'Yes! RUMIO is available on iOS and Android. Use the app to scan As-Is barcodes in-store (which helps the whole network), manage your project checklists, and receive push notifications when items go on sale.',
  },
  {
    id: '7',
    question: 'How do I contact support?',
    answer: 'You can reach our support team through the Support page on our website. We typically respond within 24 hours on business days.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const content = useWebsiteContentOptional();
  const { language, t } = useLanguage();

  // Fetch FAQs from API
  const { data: faqsResponse, isLoading, error } = useQuery({
    queryKey: ['faqs', 'landing', language],
    queryFn: () => api.getFaqs({ page: 'landing', lang: language }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Debug: Log FAQ response to help diagnose translation issues
  if (import.meta.env.DEV) {
    console.log('[FAQ] Language:', language, 'Response:', faqsResponse, 'Loading:', isLoading, 'Error:', error);
  }

  // Use API FAQs or fall back to defaults
  const faqs = faqsResponse?.data?.length ? faqsResponse.data : defaultFaqs;

  const get = (key: string, fallback: string) =>
    content?.getContent('faq', key, fallback) ?? fallback;

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <EditableElement section="faq" field="badge">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 rounded-full text-sm font-medium text-primary-700 mb-4">
              <HelpCircle className="w-4 h-4" />
              {get('badge', 'FAQ')}
            </div>
          </EditableElement>
          <EditableElement section="faq" field="title">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              {get('title', 'Got')}
              <span className="text-primary-600">
                {' '}{get('title_highlight', 'questions?')}
              </span>
            </h2>
          </EditableElement>
          <EditableElement section="faq" field="subtitle">
            <p className="mt-4 text-lg text-gray-600">
              {get('subtitle', 'Everything you need to know about RUMIO.')}
            </p>
          </EditableElement>
        </div>

        {/* FAQ accordion */}
        <div className="space-y-3" role="region" aria-label={t('faq.ariaLabel', 'Frequently Asked Questions')}>
          {faqs.map((faq: { id: string; question: string; answer: string }, index: number) => {
            const isOpen = openIndex === index;
            const headingId = `faq-heading-${index}`;
            const panelId = `faq-panel-${index}`;

            return (
              <div
                key={faq.id}
                className={`
                  rounded-xl border overflow-hidden transition-all duration-300
                  ${isOpen ? 'border-primary-200 bg-primary-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}
                `}
              >
                <h3>
                  <button
                    id={headingId}
                    className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-600 rounded-xl"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                  >
                    <span className={`font-semibold transition-colors ${isOpen ? 'text-gray-900' : 'text-gray-700'}`}>
                      {faq.question}
                    </span>
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300
                      ${isOpen ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'}
                    `}>
                      {isOpen ? (
                        <Minus className="w-4 h-4" aria-hidden="true" />
                      ) : (
                        <Plus className="w-4 h-4" aria-hidden="true" />
                      )}
                    </div>
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  className={`
                    grid transition-all duration-300 ease-in-out
                    ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                  `}
                >
                  <div className="overflow-hidden">
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact CTA */}
        <EditableElement section="faq" field="cta_text">
          <div className="mt-12 text-center p-8 bg-gray-50 rounded-2xl border border-gray-200">
            <p className="text-gray-700 mb-4">
              {get('cta_text', "Still have questions? We're here to help!")}
            </p>
            <a
              href={get('cta_link', '/support')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 hover:shadow-lg transition-all duration-300"
            >
              {get('cta_button', 'Contact Support')}
            </a>
          </div>
        </EditableElement>
      </div>
    </section>
  );
}
